<template>
  <div class="relative">
    <span
      v-tooltip="setContextTooltip"
      @click.stop="showSetContext = !showSetContext"
    >
      <slot :toggle-menu="toggle">
        <icon-base
          :icon="context ? IconEdit : IconWarning"
          :icon-color="context ? UTIL_COLORS.DEFAULT : UTIL_COLORS.WARNING"
          class="cursor-pointer"
          data-cy="set-year-context"
          @click.stop="toggle"
        />
      </slot>
    </span>
    <div
      v-if="showSetContext"
      ref="popoverRef"
      class="z-30 flex min-w-50 flex-col justify-between space-y-6 rounded-sm bg-white p-5 whitespace-nowrap shadow-sm"
    >
      <lf-dropdown
        data-cy="select-year"
        class="h-10 w-full"
        name="selectedYear"
        :options="yearOptions"
        :value="selectedYear"
        :placeholder="$t('COMMON.YEAR')"
        full-width
        @change="selectYear"
      />
      <primary-button
        :disabled="!selectedYear"
        data-cy="apply-year"
        @click="applyContext"
      >
        {{ $t("COMMON.APPLY") }}
      </primary-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { UTIL_COLORS } from "@/helpers/constants";
import { useTeleportV2 } from "@/hooks/elements";
import { useYearsOptions } from "@/hooks/orchestration";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  IContextSetValueModel,
  SpecificYear,
} from "@/models/orchestration";

const emit = defineEmits<{
  (event: "apply", value: IContextSetValueModel): void;
}>();

const { context } = defineProps<{
  context?: IContextSetValueModel;
}>();

const { t } = useI18n();
const yearOptions = useYearsOptions;
const showSetContext = ref(false);
const selectedYear = ref<number | string | null>(
  (context?.value as SpecificYear)?.year ?? null
);
const popoverRef = ref<HTMLElement | null>(null);
useTeleportV2({
  target: popoverRef,
  offset: { right: -12, top: 5 },
  checkForOverflow: true,
});

const setContextTooltip = computed(() => {
  return `${t("COMMON.SET")} ${t("SCORECARDS.SCORE_MODEL")}`;
});

const toggle = () => {
  showSetContext.value = !showSetContext.value;
};

const selectYear = (year: string | number) => {
  selectedYear.value = year;
};

const applyContext = () => {
  if (!selectedYear.value) {
    return;
  }
  const context: IContextSetValueModel = {
    type: "specific_year",
    value: {
      year: selectedYear.value,
    },
  };
  emit("apply", context);
  showSetContext.value = false;
};
</script>
